// Row contracts of the memory rows (M7-03; 02 §Knowledge, §Operating cadence, §Goals and
// reminders; 07) and the Recall webhook delivery CompanyOS receives. Same conventions as
// `rows.ts`: keys are column names, nullability is the column's, enums are the CHECK's;
// `contract-drift.test.ts` binds each to its table.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  CrewMemoryBucket,
  CrewMemorySubjectKind,
  DecisionOutcomeAnswer,
  DecisionSource,
  MemoryScopeKind,
  PromotionState,
  RecallMemoryCopyState,
  ReminderOrigin,
} from "./enums";
import { boundedRecord, Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

export const Promotion = z
  .object({
    company_id: Uuid,
    id: Uuid,
    recall_ref: z.string(),
    recall_bucket_id: z.string(),
    scope_kind: MemoryScopeKind,
    scope_id: Uuid,
    proposal_kind: z.string(),
    suggested_title: z.string(),
    body: z.string(),
    state: PromotionState,
    reviewer_seat_id: Uuid.nullable(),
    approval_id: Uuid.nullable(),
    page_id: Uuid.nullable(),
    decided_at: Timestamp.nullable(),
    decided_by_membership_id: Uuid.nullable(),
    ...stamps,
  })
  .meta({ id: "Promotion" });
export type Promotion = z.infer<typeof Promotion>;

export const Decision = z
  .object({
    company_id: Uuid,
    id: Uuid,
    title: z.string(),
    context: z.string().nullable(),
    options: z.array(z.string()),
    decision: z.string(),
    why: z.string().nullable(),
    decided_by_seat_id: Uuid.nullable(),
    review_at: Timestamp.nullable(),
    source: DecisionSource,
    source_ref: z.string().nullable(),
    share_to_portfolio: z.boolean(),
    recall_ref: z.string().nullable(),
    /** RECALL-INBOX-LOOP: what was expected to happen; `review_at` is the check date. */
    expected_outcome: z.string().nullable(),
    /** When the "did it work" card was filed (once). */
    outcome_card_at: Timestamp.nullable(),
    /** The decider's answer to the check; null until they answer. */
    outcome: DecisionOutcomeAnswer.nullable(),
    outcome_at: Timestamp.nullable(),
    outcome_by_membership_id: Uuid.nullable(),
    /** Recall's decision-outcome notice the check came from, when it did. */
    outcome_notice: z.string().nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Decision" });
export type Decision = z.infer<typeof Decision>;

export const Reminder = z
  .object({
    company_id: Uuid,
    id: Uuid,
    /** The seat reminded, or null for a person's own reminder (CHAT-SWITCH-A). */
    seat_id: Uuid.nullable(),
    /** CHAT-SWITCH-A: the person a personal reminder is for (null for a seat's). */
    membership_id: Uuid.nullable(),
    /** CHAT-SWITCH-A: a message reminder's thread. */
    thread_id: Uuid.nullable(),
    at: Timestamp,
    text: z.string(),
    subject_kind: z.string().nullable(),
    subject_id: Uuid.nullable(),
    origin: ReminderOrigin,
    origin_ref: z.string().nullable(),
    fired_at: Timestamp.nullable(),
    dismissed_at: Timestamp.nullable(),
    ...stamps,
  })
  .meta({ id: "Reminder" });
export type Reminder = z.infer<typeof Reminder>;

/** One bucket a hire-as-DE copy reads (M7-05; the crew's OQ 37 grant it came from). */
export const RecallCopySource = z
  .object({
    bucket: CrewMemoryBucket,
    subject_kind: CrewMemorySubjectKind,
    subject_id: Uuid,
    external_id: z.string().min(1).max(96),
  })
  .meta({ id: "RecallCopySource" });

/** A hire-as-DE memory copy (M7-05; 07 §Agent keys per DE). */
export const RecallMemoryCopy = z
  .object({
    company_id: Uuid,
    id: Uuid,
    de_membership_id: Uuid,
    crew_membership_id: Uuid,
    target_seat_id: Uuid,
    sources: z.array(RecallCopySource),
    state: RecallMemoryCopyState,
    created: z.int(),
    reinforced: z.int(),
    proposed: z.int(),
    attempts: z.int(),
    last_error: z.string().nullable(),
    copied_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "RecallMemoryCopy" });
export type RecallMemoryCopy = z.infer<typeof RecallMemoryCopy>;

/**
 * A DE's Recall agent as its profile shows it (M7-05; `digital-employees/{id}/recall-agent`).
 * `state`: `not_configured` (Recall is not wired to this deployment: no `RECALL_URL` or no
 * platform key), `waiting` (no agent yet: the DE has no CompanyOS agent key, or the next
 * `recall.provision` tick has not run), `pending` (a key issuance waits to be stored and
 * acknowledged), `held` (its `rk_` is held sealed), `retired` (the agent's domain ended). The key
 * itself never leaves the server; `grants` are bucket external ids, `key_for_prefix` the
 * CompanyOS agent key its `rk_` was issued or rotated for.
 */
export const RecallAgentState = z
  .object({
    membership_id: Uuid,
    agent_kind: z.enum(["digital_employee", "personal_crew"]),
    state: z.enum(["not_configured", "waiting", "pending", "held", "retired"]),
    recall_agent_id: z.string().nullable(),
    personal_access: z.literal("none"),
    grants: z.array(z.string()),
    key_for_prefix: z.string().nullable(),
    updated_at: Timestamp.nullable(),
    copy: RecallMemoryCopy.nullable(),
    /**
     * AG-MEMORY: the satellite's sync status of this agent's Recall key (the last recall-keys
     * push's answer). Absent until a push named it.
     */
    panel_keys: z
      .object({
        state: z.enum(["applied", "pending", "unknown_profile"]),
        at: Timestamp,
      })
      .optional(),
    /**
     * D14 preview (`settings.memory.hot_copy.enabled`): the agent's hot copy, when the setting
     * is on (null: none held yet, or dropped). Absent when the setting is off. Never its content.
     */
    hot_copy: z
      .object({
        size_bytes: z.int().min(0),
        fetched_at: Timestamp,
        source_spaces: z.array(z.string()),
        trimmed: z.array(z.string()),
      })
      .nullable()
      .optional(),
    /**
     * D14+D15: this agent's memories waiting for Recall (`recall_memory_outbox`), present only
     * when any wait or Recall is down. `held` counts writes held for a person or for Recall, by
     * reason (`access_revoked`, `space_gone`, `writer_gone`, `consent_withdrawn`,
     * `tenant_frozen`, `tenant_archived`, `refused`); `recall_down_since` while Recall does not
     * answer. Never a memory's words.
     */
    memory_queue: z
      .object({
        queued: z.int().min(0),
        held: z.int().min(0),
        held_reasons: z.record(z.string(), z.int().min(0)),
        recall_down_since: Timestamp.nullable(),
        tenant_hold: z.string().nullable(),
      })
      .optional(),
  })
  .meta({ id: "RecallAgentState" });
export type RecallAgentState = z.infer<typeof RecallAgentState>;

/**
 * One signed Recall webhook delivery (Recall `control/events.py:369-381`): `{id, kind, at,
 * bucket_id, data}`, `data` carrying only Recall's allow-listed fields per kind
 * (`WEBHOOK_FIELDS`, `events.py:293-300`). The route verifies `X-Recall-Signature` over the raw
 * bytes before this schema is applied.
 */
export const RecallWebhookDelivery = z
  .object({
    id: z.string().min(1).max(96),
    kind: z.string().min(1).max(64),
    at: z.string().max(64).nullable(),
    bucket_id: z.string().min(1).max(96).nullable(),
    data: boundedRecord(z.unknown()),
  })
  .meta({ id: "RecallWebhookDelivery" });
export type RecallWebhookDelivery = z.infer<typeof RecallWebhookDelivery>;

/**
 * The callback's answer: `accepted` (recorded for the `recall.callbacks` consumer), `duplicate`
 * (this Recall event id was recorded before; nothing written) or `ignored` (a kind CompanyOS
 * does not act on: logged only, 07 §Webhooks "other events").
 */
export const RecallCallbackAck = z
  .object({
    status: z.enum(["accepted", "duplicate", "ignored"]),
    kind: z.string(),
  })
  .meta({ id: "RecallCallbackAck" });
export type RecallCallbackAck = z.infer<typeof RecallCallbackAck>;
